import { parseFileUrls } from '@/modules/CustomData/utils/image.js';

/**
 * Whether finish photo is present on an AP (work shift) row.
 * @param {Object|null|undefined} item - AP table row
 * @returns {boolean}
 */
export function hasApFinishPhoto(item) {
	if (!item || typeof item !== 'object') {
		return false;
	}

	const photoFinish = item.photo_finish;
	if (photoFinish === null || photoFinish === undefined || photoFinish === '') {
		return false;
	}

	return parseFileUrls(photoFinish).length > 0;
}

/**
 * Whether an AP row is a cancelled shift (`status === 'cancelled'`).
 * Matches shift color filter gray category.
 * @param {Object|null|undefined} item - AP table row
 * @returns {boolean}
 */
export function isApShiftCancelled(item) {
	if (!item || typeof item !== 'object') {
		return false;
	}

	const raw = item.status;
	const status = typeof raw === 'object' && raw !== null
		? (raw.name ?? raw.status ?? raw.value ?? '')
		: raw;

	return String(status ?? '').trim().toLowerCase() === 'cancelled';
}

/**
 * Parse AP planned start from `date` + `start` into a local Date.
 * Supports date `DD.MM.YYYY` / `YYYY-MM-DD`, time `HH:mm` / `HH:mm:ss`.
 * @param {Object|null|undefined} item - AP table row
 * @returns {Date|null}
 */
export function parseApPlannedStartDateTime(item) {
	if (!item || typeof item !== 'object') {
		return null;
	}

	const dateRaw = item.date;
	const startRaw = item.start;
	if (dateRaw === null || dateRaw === undefined || dateRaw === '') {
		return null;
	}
	if (startRaw === null || startRaw === undefined || startRaw === '') {
		return null;
	}

	const dateStr = String(dateRaw).trim();
	const startStr = String(startRaw).trim();

	let year;
	let month;
	let day;

	const dmy = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
	const ymd = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
	if (dmy) {
		day = Number(dmy[1]);
		month = Number(dmy[2]);
		year = Number(dmy[3]);
	} else if (ymd) {
		year = Number(ymd[1]);
		month = Number(ymd[2]);
		day = Number(ymd[3]);
	} else {
		return null;
	}

	const timeMatch = startStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
	if (!timeMatch) {
		return null;
	}

	const hours = Number(timeMatch[1]);
	const minutes = Number(timeMatch[2]);
	const seconds = Number(timeMatch[3] || 0);

	if (
		!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)
		|| !Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)
		|| month < 1 || month > 12
		|| day < 1 || day > 31
		|| hours > 23 || minutes > 59 || seconds > 59
	) {
		return null;
	}

	const planned = new Date(year, month - 1, day, hours, minutes, seconds, 0);
	if (Number.isNaN(planned.getTime())) {
		return null;
	}

	// Reject overflow dates (e.g. 32.01.2026 → Feb roll)
	if (
		planned.getFullYear() !== year
		|| planned.getMonth() !== month - 1
		|| planned.getDate() !== day
	) {
		return null;
	}

	return planned;
}

/**
 * Whether planned shift start (`date` + `start`) has already arrived.
 * Missing/invalid date or start → false (audit blocked).
 * @param {Object|null|undefined} item - AP table row
 * @param {Date} [now]
 * @returns {boolean}
 */
export function hasApPlannedStartArrived(item, now = new Date()) {
	const planned = parseApPlannedStartDateTime(item);
	if (!planned) {
		return false;
	}

	return now.getTime() >= planned.getTime();
}

/**
 * Whether an AP (work shift) row allows starting an audit.
 * Blocked when: finish photo present, cancelled, or planned start not yet arrived
 * (missing date/start also blocks). photo_start is intentionally ignored.
 * @param {Object|null|undefined} item - AP table row
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isApShiftOpenForAudit(item, now = new Date()) {
	return !hasApFinishPhoto(item)
		&& !isApShiftCancelled(item)
		&& hasApPlannedStartArrived(item, now);
}
